import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { VERSION } from '../environments/version';

import {
    MsalService,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
    AuthenticationResult,
    EventMessage,
    EventType,
    InteractionStatus,
    InteractionType,
    PopupRequest,
    RedirectRequest,
} from "@azure/msal-browser";
import { Subject } from "rxjs";
import {
    setPreviousViewInStorage,
    setScreenModeInStorage,
} from "@app/utils/storage-utils";
import { filter, takeUntil, map } from "rxjs/operators";
import {
    clearGroupsInStorage,
    removeCurrentUserFromStorage,
} from "@app/utils/storage-utils";
import { GraphService } from "@app-services/graph.service";
import { environment } from "../environments/environment";
import { GlobalService } from "./services/global.services";
import { UserType } from "./models/common";
import { GlobalConstants } from "./utils/global-constants";
import { APP_BASE_HREF, PlatformLocation } from "@angular/common";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
    appTitle = "Supply Ontario";
    envLabel = environment.envLabel;
    isIframe = false;
    dashboardIframe = false;
    dashboardListIframe = false;
    loginDisplay = false;
    checkUserAttributes = false;

    userName = "";
    userEmail = "";
    testMode = environment.showTestItems;
    adminGroupUrl = environment.appGroups.adminUrl;
    userGroupUrl = environment.appGroups.userUrl;
    readOnlyGroupUrl = environment.appGroups.readOnlyUrl;
    appEmail = environment.appEmailAddress;
    appVersion = VERSION;
    
    spinnerMessage = ".. please wait ...";
    btnAllowNewRequest = true;
    miniMenuLinkClass="no-show-links";

    userType : UserType = { isAdmin: false, isUser: false, isReadOnly: false};
 
    spinnerGif: string = "assets/spinner.gif";
    globalService : GlobalService;
    private readonly _destroying$ = new Subject<void>();

    constructor(
        @Inject(MSAL_GUARD_CONFIG)       
        private msalGuardConfig: MsalGuardConfiguration,
        @Inject(APP_BASE_HREF) private baseHref:string,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private graphService: GraphService,
        private titleService: Title,
        private router: Router,
        private _globalService: GlobalService,
        private activatedRoute: ActivatedRoute,

    ) {
        this.globalService = _globalService;
        this.spinnerGif = this.baseHref + "assets/spinner.gif";

    }

    ngOnInit(): void {


        this.setLoginDisplay();
        const title = this.titleService.getTitle();
       
        
        //Look for a title value found in the routing and use it for the title.
        this.router
        .events.pipe(
          filter(event => event instanceof NavigationEnd),
          map(() => {
            let child = this.activatedRoute.firstChild;
            /*
            console.log(child?.firstChild)
            console.log(child?.snapshot.data['title'])
            */
            while (child?.firstChild) {
              child = child?.firstChild;
            }
            if (child?.snapshot.data['title']) {
              return child?.snapshot.data['title'];
            }
            


            return title;
          })
        ).subscribe((ttl) => {
          this.titleService.setTitle(ttl+"");
          this.appTitle = ttl+"";
          //This is way to not show the new request button on various screens using screen title.
          this.btnAllowNewRequest = !(ttl === 'Page Not Found' || ttl === 'Enterprise Technology Hub' || ttl === 'Work Request Details' 
                                 || ttl === 'New Work Request' || ttl === 'User Profile' || ttl === 'Statistics' 
                                 || ttl === 'Work Order Feedback'
                                 || ttl === 'Status Report' || ttl.indexOf("Project") > -1);
        });
    
        //if in an Iframe (The Source), we need to put in some formatting
        this.isIframe = window !== window.parent && !window.opener;
        if (window.parent.frames.length > 0 && window.parent.frames[0].document.URL.indexOf("project-dashboard") > -1) {
            this.dashboardIframe = true;
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const scale = urlParams.get('iframescale')||.8;
            document.body.style.transform = "scale(" + scale + ")";
            document.body.style.transformOrigin = "top left";
            this.globalService.iframeScale = +(urlParams.get('iframescale')||0);
            this.globalService.statusReportScale = +(urlParams.get('reportscale')||0);
            
           
            this.btnAllowNewRequest = false;
        }
        if (window.parent.frames.length > 0 && window.parent.frames[0].document.URL.indexOf("project-list") > -1) {
            this.dashboardListIframe = true;
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const scale = urlParams.get('iframescale')||.8;
            this.globalService.iframeScale = +(urlParams.get('iframescale')||0);
            this.globalService.statusReportScale = +(urlParams.get('reportscale')||0);
            document.body.style.transform = "scale(" + scale + ")";
            document.body.style.transformOrigin = "top left";
            this.btnAllowNewRequest = false;
        }
        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

        /**
         * You can subscribe to MSAL events as shown below. For more information, visit:
         * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
         */

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACCOUNT_ADDED ||
                        msg.eventType === EventType.ACCOUNT_REMOVED
                )
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = "/";
                } else {
                    this.setLoginDisplay();
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                this.checkAndSetActiveAccount();
            });
    }

    setDocTitle(title: string) {
        console.log('current title:::::' + this.titleService.getTitle());
        this.titleService.setTitle(title);
     }

    setLoginDisplay() {
        this.loginDisplay =
            this.authService.instance.getAllAccounts().length > 0;
        this.setUserName();
    }

    addWorkRequest(): void {
        setScreenModeInStorage(GlobalConstants.REQUEST_FORM_MODE_USER);
        setPreviousViewInStorage(this.router.url);
        const route = "/work-request/new";
        this.router.navigateByUrl(route, {});
    }
    setUserName() {
        if (this.authService.instance.getActiveAccount()) {
            this.userName = this.authService.instance.getActiveAccount()?.name ||'';
            this.userEmail =
                this.authService.instance.getActiveAccount()?.username||'';
            this.graphService.getCurrentUser();
            if (!this.checkUserAttributes) {
                this.checkUserAttributes = true;
                this.globalService.checkUserAttributes();                
            }
            this.userType = this.globalService.checkGroupAccess();
        }
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        const activeAccount = this.authService.instance.getActiveAccount();

        if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
        ) {
            const accounts = this.authService.instance.getAllAccounts();
            // add your code for handling multiple accounts here
            this.authService.instance.setActiveAccount(accounts[0]);
            this.setUserName();
        }
    }

    login() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService
                    .loginPopup({
                        ...this.msalGuardConfig.authRequest,
                    } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(
                            response.account
                        );
                    });
            } else {
                this.authService
                    .loginPopup()
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(
                            response.account
                        );
                    });
            }
        } else {
            /*
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({
                    ...this.msalGuardConfig.authRequest,
                } as PopupRequest);
            } else {
                this.authService.loginRedirect();
            }
            */
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({
                    ...this.msalGuardConfig.authRequest,
                } as RedirectRequest);
            } else {
                this.authService.loginRedirect();
            }
        }
    }

    logout() {
        const activeAccount =
            this.authService.instance.getActiveAccount() ||
            this.authService.instance.getAllAccounts()[0];
        clearGroupsInStorage(activeAccount); // make sure to remove groups from storage

        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({
                account: activeAccount,
            });
        } else {
            this.authService.logoutRedirect({
                account: activeAccount,
            });
        }

        removeCurrentUserFromStorage();
    }

    showMiniMenuOptions() {
        if (this.miniMenuLinkClass === 'show-links') {
            this.miniMenuLinkClass = 'no-show-links';
        } else {
            this.miniMenuLinkClass = 'show-links';
        }
    }
    // unsubscribe to events when component is destroyed
    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
